'use es6';

import { byName as TemplateTypes } from 'ContentUtils/constants/TemplateTypes';
import ImmutableModel from 'DesignData/models/ImmutableModel';
import PortalCopyTypes from 'DesignData/enums/PortalCopyTypes';
/**
 * Convert a `type:widget_container` widget to have the same structure as other layout nodes.
 * Moves the widgets in `widgets:[]` to `rows:[]` in the same structure that `type:cell` widget uses.
 *
 * @param {Object} node   Widget node. Pass by reference.
 * @return {Object} Object node, the same passed in.
 */

function convertFlexColumnNodeToRegularLayoutNode(node) {
  if (node.type !== 'widget_container') {
    return node;
  }

  node.rows = !(node.widgets && node.widgets.length) ? [] : node.widgets.map(widget => ({
    0: Object.assign({}, widget, {
      x: 0,
      w: 12
    })
  }));
  node.widgets = undefined;
  return node;
}
/**
 * Convert a `type:flex_area` or `type:email_flex_area` widget to have the same structure as other layout nodes.
 * Moves the structure under `params.default_layout` to the same structure that `type:cell` widget uses.
 *
 * @param {Object} node   Widget node. Pass by reference.
 * @return {Object} Object node, the same passed in.
 */


function convertFlexAreaNodeToRegularLayoutNode(node) {
  if (!(node.type === 'flex_area' || node.type === 'email_flex_area')) {
    return node;
  }

  const hasDefaultLayout = node.params && node.params.default_layout;

  if (hasDefaultLayout) {
    node.rows = node.params.default_layout.rows || [];
    node.params.default_layout = undefined;
  } else {
    node.rows = [];
  } // eslint-disable-next-line no-use-before-define


  Layout.forEach(node, widget => {
    if (widget.type !== 'cell') return;
    widget.rows = !(widget.widgets && widget.widgets.length) ? [] : widget.widgets.map(widg => ({
      0: Object.assign({}, widg, {
        x: 0,
        w: 12
      })
    }));
    widget.widgets = undefined;
  });
  node.widgets = undefined;
  return node;
}
/**
 * Convert a `type:widget_container` node from the consistent cell like
 * structure used on the frontend to the inconsistent structure used on the backend.
 *
 * @param {Object} node   Widget node. Pass by reference.
 * @return {Object} Object node, the same passed in.
 */


function convertRegularLayoutNodeToFlexColumnNode(node) {
  if (node.type !== 'widget_container') {
    return node;
  }

  node.widgets = node.rows.map(row => row[0]);
  node.rows = undefined;
  return node;
}
/**
 * Convert a `type:flex_area` or `type:email_flex_area` node from the consistent cell like
 * structure used on the frontend to the inconsistent structure used on the backend.
 *
 * @param {Object} node   Widget node. Pass by reference.
 * @return {Object} Object node, the same passed in.
 */


function convertRegularLayoutNodeToFlexAreaNode(node) {
  if (!(node.type === 'flex_area' || node.type === 'email_flex_area')) {
    return node;
  } // eslint-disable-next-line no-use-before-define


  Layout.forEach(node, cell => {
    if (cell.type !== 'cell') return;
    cell.widgets = cell.rows.map(row => row[0]);
    cell.rows = undefined;
  });
  node.name = 'main'; // This is a requirement on backend.

  node.params = {
    default_layout: {
      rows: node.rows,
      name: 'flexareamodule',
      type: 'cell',
      x: 0,
      w: 12
    }
  };
  node.rows = undefined;
  node.widgets = undefined;
  return node;
}
/**
 * Wrapper class of Layout data.
 *
 * @class
 * @abstract
 * @extends DesignData/models/ImmutableModel
 */


export default class Layout extends ImmutableModel {
  /**
   * @constructor
   * @param {Object} props
   */
  constructor(__props) {
    const props = Object.assign({}, __props, {
      id: __props.id || __props.builtinId,
      meta: __props.meta || {},
      label: __props.templateType === TemplateTypes.global_group.id ? __props.label.split('/').pop() : __props.label
    });
    return super(props);
  }

  getPortalCopyType() {
    return PortalCopyTypes.LAYOUT;
  }
  /**
   * IMPORT helper.
   * Recurses a layout tree, converting Flex types (`widget_container`, `flex_area`, `email_flex_area`) to regular
   * layout node structures. This allows us to have a consistent tree structure on the frontend.
   *
   * @param {Object} layoutData  Root node of tree. Pass by reference.
   * @return {Object} Object with a consistent node data structure.
   */


  static convertFlexNodesToRegularLayoutNodes(layoutData) {
    Layout.forEach(layoutData, node => {
      switch (node.type) {
        case 'widget_container':
          convertFlexColumnNodeToRegularLayoutNode(node);
          break;

        case 'flex_area':
        case 'email_flex_area':
          convertFlexAreaNodeToRegularLayoutNode(node);
          break;

        default:
          break;
      }
    });
    return layoutData;
  }
  /**
   * EXPORT helper.
   * Recurses a layout tree, converting Flex types (`widget_container`, `flex_area`, `email_flex_area`) to the irregular
   * layout node structures used on the server.
   *
   * @param {Object} layoutData  Root node of tree. Pass by reference.
   * @return {Object} Object with a consistent node data structure.
   */


  static convertRegularLayoutNodesToFlexNodes(layoutData) {
    Layout.forEach(layoutData, node => {
      switch (node.type) {
        case 'widget_container':
          convertRegularLayoutNodeToFlexColumnNode(node);
          break;

        case 'flex_area':
        case 'email_flex_area':
          convertRegularLayoutNodeToFlexAreaNode(node);
          break;

        default:
          break;
      }
    });
    return layoutData;
  }
  /**
   * Functionally similar to Array.prototype.forEach() but for the tree data of layouts.
   *
   * @param {Object} layoutData   A cell or widget to begin searching from.
   * @param {Function} callback   Passed a widget object.
   */


  static forEach(layoutData, callback) {
    callback(layoutData);

    if (!layoutData.rows) {
      return;
    }

    layoutData.rows.forEach(widgets => {
      widgets = Object.values(widgets || {});
      return widgets.forEach(widget => Layout.forEach(widget, callback));
    });
  }
  /**
   * Functionally similar to Array.prototype.some() but for the tree data of layouts.
   *
   * @param {Object} layoutData   A cell or widget to begin searching from.
   * @param {Function} callback   Passed a widget object. Should return true or false.
   * @return {Boolean} True if callback return truthy at least once, false otherwise.
   */


  static some(layoutData, callback) {
    if (callback(layoutData)) {
      return true;
    }

    if (!layoutData.rows) {
      return false;
    }

    return layoutData.rows.some(widgets => {
      widgets = Object.values(widgets || {});
      return widgets.some(widget => Layout.some(widget, callback));
    });
  }
  /**
   * Grabs associated content, returns empty array if there is none
   */


  getAssociatedContent() {
    return this.meta && this.meta.associatedContent || [];
  }

}
/**
 * Values that may be missing from a layout object returned by API.
 *
 * @override
 * @constant
 * @static
 */

Layout.DEFAULT_FIELDS = Object.freeze({
  enableDomainStylesheets: null,
  includeDefaultCustomCss: null
});